/**
 * Footer accordions (mobile only)
 * ---------------------------------------------------------------------------
 * Progressive enhancement, kept out of the generated theme files so a Horizon
 * update cannot overwrite it. Loaded from snippets/scripts.liquid, styled by
 * the "Footer accordions" block in assets/custom.css.
 *
 * Below 750px every footer column that starts with a heading - the rich text
 * blocks and the menu block - is turned into a collapsible panel. Only one
 * panel can be open at a time: opening one closes the others. On desktop the
 * original markup is restored, so the footer keeps its normal column layout.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';
  var HEADINGS = 'h1,h2,h3,h4,h5,h6';
  var CANDIDATES = '.text-block, accordion-custom.menu';

  var mobile = window.matchMedia(MOBILE);
  var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var items = [];
  var counter = 0;

  /** Footer section wrappers on the page. */
  function footers() {
    var found = document.querySelectorAll('.shopify-section-group-footer-group');
    return found.length ? found : document.querySelectorAll('footer.shopify-section, footer.section-wrapper');
  }

  /**
   * Works out what to use as the trigger and what to collapse.
   * @param {Element} el
   * @returns {{trigger: Element, content: Element|null, mode: string}|null}
   */
  function parts(el) {
    if (el.matches('accordion-custom.menu')) {
      var summary = el.querySelector('summary');
      var content = el.querySelector('.details-content');
      if (!summary || !content || !content.children.length) return null;
      return { trigger: summary, content: content, mode: 'details' };
    }

    var heading = el.firstElementChild;
    if (!heading || !heading.matches(HEADINGS) || !heading.nextElementSibling) return null;
    return { trigger: heading, content: null, mode: 'text' };
  }

  function enhance(el) {
    if (el.footerAccordion) return;

    var found = parts(el);
    if (!found) return;

    var panel = document.createElement('div');
    panel.className = 'footer-acc__panel';
    panel.id = 'footer-acc-panel-' + ++counter;

    var item = {
      root: el,
      trigger: found.trigger,
      panel: panel,
      mode: found.mode,
      details: null,
      open: false,
      onEnd: null
    };

    if (found.mode === 'details') {
      // <details> stays permanently open - the height animation is ours, so
      // anything that tries to close it (the theme's accordion-custom, a
      // native summary click) gets overruled.
      item.details = el.querySelector('details');
      if (item.details) {
        item.details.open = true;
        item.onToggle = function () {
          if (item.details && !item.details.open) item.details.open = true;
        };
        item.details.addEventListener('toggle', item.onToggle);
      }
      found.content.parentNode.insertBefore(panel, found.content);
      panel.appendChild(found.content);
    } else {
      var inner = document.createElement('div');
      inner.className = 'footer-acc__inner';
      var node = found.trigger.nextSibling;
      while (node) {
        var next = node.nextSibling;
        inner.appendChild(node);
        node = next;
      }
      panel.appendChild(inner);
      el.appendChild(panel);
    }

    el.classList.add('footer-acc');
    if (el.parentElement) el.parentElement.classList.add('footer-acc-list');
    found.trigger.classList.add('footer-acc__trigger');
    found.trigger.setAttribute('aria-expanded', 'false');
    found.trigger.setAttribute('aria-controls', panel.id);
    if (found.trigger.tagName !== 'SUMMARY') {
      found.trigger.setAttribute('role', 'button');
      found.trigger.setAttribute('tabindex', '0');
    }

    item.onClick = function (event) {
      event.preventDefault();
      toggle(item);
    };
    item.onKeyDown = function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      toggle(item);
    };

    found.trigger.addEventListener('click', item.onClick);
    found.trigger.addEventListener('keydown', item.onKeyDown);

    el.footerAccordion = item;
    items.push(item);
  }

  function destroy(item) {
    var el = item.root;
    var panel = item.panel;

    item.trigger.removeEventListener('click', item.onClick);
    item.trigger.removeEventListener('keydown', item.onKeyDown);
    if (item.details && item.onToggle) item.details.removeEventListener('toggle', item.onToggle);
    clearEnd(item);

    if (item.mode === 'details') {
      panel.parentNode.insertBefore(panel.firstElementChild, panel);
    } else {
      var inner = panel.firstElementChild;
      while (inner && inner.firstChild) el.appendChild(inner.firstChild);
    }
    panel.parentNode.removeChild(panel);

    el.classList.remove('footer-acc', 'is-open');
    item.trigger.classList.remove('footer-acc__trigger');
    item.trigger.removeAttribute('aria-expanded');
    item.trigger.removeAttribute('aria-controls');
    if (item.trigger.tagName !== 'SUMMARY') {
      item.trigger.removeAttribute('role');
      item.trigger.removeAttribute('tabindex');
    }

    var parent = el.parentElement;
    if (parent && !parent.querySelector('.footer-acc')) parent.classList.remove('footer-acc-list');

    delete el.footerAccordion;
  }

  function clearEnd(item) {
    if (!item.onEnd) return;
    item.panel.removeEventListener('transitionend', item.onEnd);
    item.onEnd = null;
  }

  function afterHeight(item, callback) {
    clearEnd(item);
    item.onEnd = function (event) {
      if (event.target !== item.panel || event.propertyName !== 'height') return;
      clearEnd(item);
      callback();
    };
    item.panel.addEventListener('transitionend', item.onEnd);
  }

  function expand(item) {
    var panel = item.panel;

    item.open = true;
    item.root.classList.add('is-open');
    item.trigger.setAttribute('aria-expanded', 'true');
    clearEnd(item);

    if (motion.matches) {
      panel.style.height = 'auto';
      return;
    }

    panel.style.height = panel.scrollHeight + 'px';
    afterHeight(item, function () {
      // Release to auto so the panel reflows with the viewport while open.
      if (item.open) panel.style.height = 'auto';
    });
  }

  function collapse(item) {
    var panel = item.panel;

    item.open = false;
    item.root.classList.remove('is-open');
    item.trigger.setAttribute('aria-expanded', 'false');
    clearEnd(item);

    if (motion.matches) {
      panel.style.height = '';
      return;
    }

    // Pin the current height so the transition has somewhere to start from.
    panel.style.height = panel.scrollHeight + 'px';
    void panel.offsetHeight;
    requestAnimationFrame(function () {
      if (!item.open) panel.style.height = '0px';
    });
  }

  function toggle(item) {
    if (item.open) {
      collapse(item);
      return;
    }

    // One open at a time.
    items.forEach(function (other) {
      if (other !== item && other.open) collapse(other);
    });
    expand(item);
  }

  function enhanceAll() {
    footers().forEach(function (footer) {
      footer.querySelectorAll(CANDIDATES).forEach(enhance);
    });
  }

  function destroyAll() {
    items.splice(0).forEach(destroy);
  }

  function apply() {
    if (mobile.matches) enhanceAll();
    else destroyAll();
  }

  apply();

  mobile.addEventListener('change', apply);

  document.addEventListener('shopify:section:load', function () {
    // Drop items whose markup was replaced by the editor, then re-scan.
    items = items.filter(function (item) {
      return item.root.isConnected;
    });
    apply();
  });

  // Theme editor: reveal the panel a merchant just selected.
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest ? event.target.closest('.footer-acc') : null;
    if (root && root.footerAccordion && !root.footerAccordion.open) toggle(root.footerAccordion);
  });
})();
